import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Factory, HardHat, Building2, X } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import CustomStandardsPanel from "./CustomStandardsPanel";

const DOMAIN_ICONS = { process_plant: Factory, infrastructure: HardHat, real_estate: Building2 };

const STEPS = [
  { id: "basics",   title: "Project basics" },
  { id: "domain",   title: "Domain" },
  // Industry + sub-type merged into a single step (Step 3) — picking
  // a sub-type below auto-derives the industry tag for analytics.
  { id: "sub_type", title: "Sub-type (drives wizard + stages + codes)" },
  { id: "phase",    title: "Project phase" },
  { id: "country",  title: "Region & code set" },
  { id: "standards",title: "Custom standards" },
  { id: "review",   title: "Review & create" },
];

// Sub-type → industry derivation. The fine-grained `sub_type` drives
// every downstream config (stage gates, codes, drawings, KPIs);
// `industry` is purely an analytics tag, derived here so the merged
// wizard step doesn't need to ask the user twice.
const SUBTYPE_TO_INDUSTRY = {
  // residential sector
  villa: "res_low", low_rise: "res_low", row_house: "res_low",
  mid_rise: "res_high", high_rise: "res_high",
  // commercial sector
  office: "commercial", it_park: "commercial", coworking: "commercial",
  retail_mall: "retail", high_street: "retail",
  // industrial sector
  warehouse: "industrial", factory: "industrial",
  cold_storage: "industrial", data_center: "data_center",
  // hospitality sector
  hotel: "hotel", resort: "hotel", convention: "hotel",
  // institutional sector
  hospital: "hospital", clinic: "hospital",
  school: "educational", university: "educational", college: "educational",
  government: "commercial", religious: "commercial",
  // mixed_use sector
  mixed_use_dev: "mixed_use", tod: "mixed_use",
  // sports sector
  stadium: "sports", sports_complex: "sports",
  convention_centre: "hotel",
};

// Wave AG — Process-Plant project phases per
// `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §1.
const PHASE_OPTIONS = [
  { id: "concept",  name: "Concept / Feasibility", accuracy: "±30-50%", duration: "2-8 weeks",
    blurb: "Preliminary sketches + block flow diagrams + order-of-magnitude CAPEX." },
  { id: "pre_feed", name: "Pre-FEED (Conceptual Design)", accuracy: "±20-25%", duration: "2-4 months",
    blurb: "Budgetary design basis + PFDs + preliminary equipment sizing + budgetary CAPEX." },
  { id: "feed",     name: "FEED (Front-End Engineering Design)", accuracy: "±10-15%", duration: "4-8 months",
    blurb: "Frozen basis + IFR P&IDs + equipment data sheets + HAZOP + plot plan + FEED CAPEX." },
  { id: "detail",   name: "Detailed Engineering", accuracy: "±5%", duration: "8-18 months",
    blurb: "IFC drawings + 3D model + isometrics + MTO + procurement + construction-ready." },
];

export default function ProjectWizardModal({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // SaaS access scope — { unrestricted, domains:[], sectors:[] }
  // Drives domain visibility + sector filtering so users only see the
  // industry + sub-industries they picked at signup.
  const [accessScope, setAccessScope] = useState(null);

  // form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState("process_plant");
  const [industry, setIndustry] = useState("og_downstream");
  const [phase, setPhase] = useState("feed");
  const [country, setCountry] = useState("IN");
  const [createdProject, setCreatedProject] = useState(null);  // used for standards step
  // Wave BM — Industry-adaptive sub-type selection (128 sub-types across 3 domains)
  const [subType, setSubType] = useState("");
  const [subTypeTax, setSubTypeTax] = useState(null);   // {process_plant:{sector:{sub:label}}, ...}
  const [subTypePreview, setSubTypePreview] = useState(null); // returned config preview
  // P0-MVP-HONEST-PRODUCT Fix 2.5 — list of wired infra sub_types from
  // /api/infra/sub-types. Drives the ENGINE READY badge in the selector
  // grid. Empty array on fetch failure (badge falls back silently).
  const [wiredInfraSubTypes, setWiredInfraSubTypes] = useState([]);
  // Spec §4.3 — previous-phase import
  const [prevOutputMode, setPrevOutputMode] = useState("none"); // none | rachana | individual
  const [rachanaFile, setRachanaFile] = useState(null);
  const [prevFiles, setPrevFiles] = useState({}); // {dbd, pfd, hmb, equipment_list, plot_plan, preliminary_pid, cost_estimate, material_report}

  useEffect(() => {
    if (!open) return;
    setStep(0); setErr(""); setName(""); setDescription("");
    setDomain("process_plant"); setIndustry("og_downstream"); setPhase("feed"); setCountry("IN");
    setCreatedProject(null);
    setSubType(""); setSubTypePreview(null);
    setPrevOutputMode("none"); setRachanaFile(null); setPrevFiles({});
    (async () => {
      try {
        // P0-MVP-HONEST-PRODUCT Fix 2.5 — pull the wired-sub_types list
        // alongside the taxonomy so the selector can render an honest
        // "ENGINE READY" / "NOT YET WIRED" badge next to each option.
        const [{ data: ind }, { data: tax }, wiredRes, scopeRes] = await Promise.all([
          api.get("/industries"),
          api.get("/industry/taxonomy"),
          api.get("/infra/sub-types").catch(() => ({ data: { mounted: [] } })),
          api.get("/auth/access-scope").catch(() => ({ data: null })),
        ]);
        setMeta(ind);
        setSubTypeTax(tax);
        setWiredInfraSubTypes(Array.isArray(wiredRes?.data?.mounted)
                                ? wiredRes.data.mounted : []);
        setAccessScope(scopeRes?.data || null);
        // Pre-pick the user's only allowed domain so the wizard starts
        // them on the correct branch immediately.
        if (scopeRes?.data && !scopeRes.data.unrestricted
              && scopeRes.data.domains?.length === 1) {
          setDomain(scopeRes.data.domains[0]);
        }
      } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    })();
  }, [open]);

  // Reset sub-type when domain changes
  useEffect(() => { setSubType(""); setSubTypePreview(null); }, [domain]);

  // Fetch sub-type config preview whenever user selects one
  useEffect(() => {
    if (!subType) { setSubTypePreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/industry/config/${domain}/${encodeURIComponent(subType)}?country=${country}`);
        if (!cancelled) setSubTypePreview(data);
      } catch { if (!cancelled) setSubTypePreview(null); }
    })();
    return () => { cancelled = true; };
  }, [subType, domain, country]);

  const filteredIndustries = useMemo(() => {
    if (!meta) return [];
    return meta.industries.filter((i) => i.domain === domain);
  }, [meta, domain]);

  // Reset industry when domain changes — first available industry for the domain
  useEffect(() => {
    const first = filteredIndustries[0];
    if (first && !filteredIndustries.find((i) => i.id === industry)) {
      setIndustry(first.id);
    }
  }, [filteredIndustries]); // eslint-disable-line

  // Auto-derive `industry` from the chosen sub-type (Step 3 merge —
  // user no longer picks industry separately). Falls back to first
  // industry for the domain if the sub-type is outside the map.
  useEffect(() => {
    if (!subType) return;
    const mapped = SUBTYPE_TO_INDUSTRY[subType];
    if (mapped) {
      // Only set if it's a valid industry for this domain
      const valid = filteredIndustries.find((i) => i.id === mapped);
      if (valid && industry !== mapped) setIndustry(mapped);
    }
  }, [subType, filteredIndustries]); // eslint-disable-line

  if (!open) return null;

  const stepDef = STEPS[step];
  const canNext = (() => {
    if (stepDef.id === "basics") return name.trim().length > 0;
    if (stepDef.id === "sub_type") return !!subType;
    if (stepDef.id === "standards") return !!createdProject;
    return true;
  })();

  const next = async () => {
    setErr("");
    if (stepDef.id === "country") {
      // Create project at end of step 5 so standards can be uploaded against the real id
      setBusy(true);
      try {
        const { data } = await api.post("/projects", {
          name: name.trim(),
          description: description.trim(),
          domain, industry, country,
          sub_type: subType || undefined,
          phase: domain === "process_plant" ? phase : undefined,
        });
        setCreatedProject(data);
        // Spec §4.3 — post-create phase import (Option 1 or Option 2)
        try {
          if (prevOutputMode === "rachana" && rachanaFile) {
            const form = new FormData();
            form.append("file", rachanaFile);
            await api.post(`/projects/${data.id}/phase/import`, form);
          } else if (prevOutputMode === "individual") {
            const any = Object.values(prevFiles).some(Boolean);
            if (any) {
              const form = new FormData();
              for (const [k, f] of Object.entries(prevFiles)) {
                if (f) form.append(k, f);
              }
              await api.post(`/projects/${data.id}/phase/import-individual`, form);
            }
          }
        } catch (impErr) {
          console.warn("Previous-phase import failed (non-fatal):", impErr);
        }
        setStep(step + 1);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || e.message);
      } finally { setBusy(false); }
      return;
    }
    // Skip Phase step for non-process_plant domains
    if (STEPS[step + 1]?.id === "phase" && domain !== "process_plant") {
      setStep((s) => Math.min(STEPS.length - 1, s + 2));
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const prev = () => {
    const target = Math.max(0, step - 1);
    if (STEPS[target]?.id === "phase" && domain !== "process_plant") {
      setStep(Math.max(0, target - 1));
    } else {
      setStep(target);
    }
  };

  const finish = () => {
    onCreated?.(createdProject);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 80,
        background: "rgba(10,14,26,0.78)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      data-testid="project-wizard"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="pf-card pf-grain" style={{ width: 760, maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "20px 26px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid var(--pf-border)" }}>
          <div>
            <div className="pf-overline" style={{ marginBottom: 4 }}>NEW PROJECT · STEP {step + 1} OF {STEPS.length}</div>
            <h2 className="pf-heading" style={{ fontSize: 22, margin: 0 }}>{stepDef.title}</h2>
          </div>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy} data-testid="wizard-close" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        {/* Step indicator */}
        <div style={{ padding: "10px 26px", display: "flex", gap: 6, borderBottom: "1px solid var(--pf-border)", background: "var(--pf-surface-2)" }}>
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              style={{
                flex: 1, height: 4, borderRadius: 2,
                background: i <= step ? "var(--pf-spark)" : "var(--pf-border)",
                transition: "background .2s",
              }}
            />
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: 26, overflow: "auto", flex: 1 }}>
          {err && <div className="pf-sev pf-sev-error" style={{ display: "block", padding: "8px 10px", marginBottom: 14 }} data-testid="wizard-error">{err}</div>}

          {stepDef.id === "basics" && (
            <div>
              <label className="pf-label">PROJECT NAME</label>
              <input className="pf-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vadinar Phase-3 expansion" autoFocus data-testid="wizard-name" />
              <div style={{ height: 14 }} />
              <label className="pf-label">DESCRIPTION (OPTIONAL)</label>
              <textarea className="pf-input pf-textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One-line summary…" data-testid="wizard-description" />
            </div>
          )}

          {stepDef.id === "domain" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              {(meta?.domains || []).filter((d) =>
                  !accessScope || accessScope.unrestricted
                    || accessScope.domains?.includes(d.id)
                ).map((d) => {
                const Icon = DOMAIN_ICONS[d.id] || Factory;
                const active = domain === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setDomain(d.id)}
                    className="pf-card"
                    data-testid={`wizard-domain-${d.id}`}
                    data-active={active}
                    style={{
                      padding: 18, textAlign: "left", cursor: "pointer",
                      background: active ? "var(--pf-surface-2)" : "var(--pf-surface)",
                      borderColor: active ? "var(--pf-spark)" : "var(--pf-border)",
                      transition: "all .15s",
                    }}
                  >
                    <Icon size={28} color={active ? "var(--pf-spark)" : "var(--pf-text-muted)"} />
                    <div className="pf-heading" style={{ fontSize: 16, margin: "10px 0 4px" }}>{d.name}</div>
                    <div style={{ fontSize: 12, color: "var(--pf-text-muted)", lineHeight: 1.45 }}>{d.tagline}</div>
                  </button>
                );
              })}
            </div>
          )}

          {stepDef.id === "sub_type" && (
            <div data-testid="wizard-sub-type-step">
              <p style={{ color: "var(--pf-text-muted)", fontSize: 13, marginTop: 0 }}>
                Pick the exact sub-type — this selection drives the wizard questions, stage-gate phases, design basis template, drawing list, codes, and KPI targets for your project.
              </p>
              {!subTypeTax ? (
                <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>Loading taxonomy…</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, maxHeight: 280, overflow: "auto", paddingRight: 6 }}
                      data-testid="wizard-sub-type-grid">
                  {Object.entries(subTypeTax.taxonomy?.[domain] || {})
                    .filter(([sectorKey]) =>
                      !accessScope || accessScope.unrestricted
                        || (accessScope.sectors || []).includes(sectorKey))
                    .map(([sectorKey, sectorData]) => {
                    const children = sectorData.sub_industries || sectorData.sub_types || {};
                    // Phase 5 close-out (Feb 2026) — for the infrastructure
                    // domain, hide sub-types whose engines are not yet
                    // implemented. Only the wired sub-types (NH/State/
                    // Expressway/Bridge/Airport) appear in the picker.
                    // Other domains show everything (RE / process_plant
                    // sub-types ship with their engines).
                    const visibleChildren = (domain === "infrastructure")
                      ? Object.fromEntries(
                          Object.entries(children).filter(
                            ([k]) => wiredInfraSubTypes.includes(k)))
                      : children;
                    // Skip the entire sector card if no wired sub-types
                    // remain after filtering.
                    if (domain === "infrastructure"
                        && Object.keys(visibleChildren).length === 0) {
                      return null;
                    }
                    return (
                      <div key={sectorKey} className="pf-card"
                            style={{ padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                        <div className="pf-overline" style={{ marginBottom: 4 }}>
                          {sectorData.label}
                        </div>
                        {Object.entries(visibleChildren).map(([key, label]) => {
                          const active = subType === key;
                          // Fix 2.5 — wired-list-driven badge. Only infra
                          // domain options surface the badge today; process-
                          // plant / real-estate options skip it.
                          const showBadge = domain === "infrastructure";
                          const isWired = showBadge
                            && wiredInfraSubTypes.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setSubType(key)}
                              data-testid={`wizard-sub-type-${key}`}
                              style={{
                                padding: "6px 10px", fontSize: 11.5, textAlign: "left",
                                background: active ? "var(--pf-spark)" : "var(--pf-surface)",
                                color: active ? "#0E1218" : "var(--pf-fg)",
                                border: `1px solid ${active ? "var(--pf-spark)" : "var(--pf-border)"}`,
                                borderRadius: 3, cursor: "pointer",
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", gap: 8,
                              }}
                            >
                              <span style={{ flex: 1, minWidth: 0,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis" }}>
                                {label}
                              </span>
                              {showBadge && (
                                <span
                                  data-testid={`wizard-sub-type-${key}-badge`}
                                  style={{
                                    fontFamily: "'JetBrains Mono', monospace",
                                    fontSize: 9, letterSpacing: 0.4,
                                    padding: "2px 5px", borderRadius: 2,
                                    flexShrink: 0,
                                    background: isWired
                                      ? "rgba(16, 185, 129, 0.18)"
                                      : "rgba(120, 120, 120, 0.18)",
                                    color: isWired
                                      ? "#10b981"
                                      : "var(--pf-text-muted)",
                                    border: isWired
                                      ? "1px solid rgba(16, 185, 129, 0.4)"
                                      : "1px solid rgba(120, 120, 120, 0.3)",
                                    textTransform: "uppercase",
                                  }}
                                  title={isWired
                                    ? "Calculation engine is fully wired"
                                    : "Engine not yet implemented; project can be created but Run buttons will be disabled"}
                                >
                                  {isWired ? "ENGINE READY" : "NOT YET WIRED"}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
              {subTypePreview && (
                <div className="pf-card" style={{ marginTop: 14, padding: 12 }}
                      data-testid="wizard-sub-type-preview">
                  <div className="pf-overline" style={{ marginBottom: 6 }}>PREVIEW</div>
                  <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 4, columnGap: 10, fontSize: 12 }}>
                    <div className="pf-label" style={{ margin: 0 }}>STAGE-GATE</div>
                    <div data-testid="wizard-preview-pattern">{subTypePreview.stagegate_pattern} ({subTypePreview.stagegate?.phases?.length || 0} phases)</div>
                    <div className="pf-label" style={{ margin: 0 }}>WIZARD Q</div>
                    <div data-testid="wizard-preview-q-count">{subTypePreview.wizard_questions?.length || 0}</div>
                    <div className="pf-label" style={{ margin: 0 }}>DRAWINGS</div>
                    <div>{subTypePreview.drawing_types?.length || 0}</div>
                    <div className="pf-label" style={{ margin: 0 }}>CODES</div>
                    <div style={{ fontSize: 11, color: "var(--pf-text-muted)", lineHeight: 1.45 }}>
                      {Object.entries(subTypePreview.codes || {}).slice(0, 4).map(([k, v]) => (
                        <div key={k}><strong style={{ color: "var(--pf-fg)" }}>{k}:</strong> {typeof v === "string" ? v.slice(0, 90) : JSON.stringify(v).slice(0, 90)}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {stepDef.id === "phase" && (
            <div data-testid="wizard-phase-step">
              <p style={{ color: "var(--pf-text-muted)", fontSize: 13, marginTop: 0 }}>
                Pick the engineering phase for this project. Capexara Process Industry gates sub-stages per the selected phase — you can advance to the next phase at any time. Cost-estimate accuracy improves with every phase.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {PHASE_OPTIONS.map((p) => {
                  const active = phase === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPhase(p.id)}
                      className="pf-card"
                      data-testid={`wizard-phase-${p.id}`}
                      style={{
                        padding: 14, textAlign: "left", cursor: "pointer",
                        borderColor: active ? "var(--pf-spark)" : "var(--pf-border)",
                        background: active ? "var(--pf-surface-2)" : "var(--pf-surface)",
                      }}
                    >
                      <div className="pf-heading" style={{ fontSize: 14, margin: 0 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "var(--pf-text-muted)", marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>
                        CAPEX {p.accuracy} · {p.duration}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--pf-text)", marginTop: 8, lineHeight: 1.45 }}>{p.blurb}</div>
                    </button>
                  );
                })}
              </div>

              {/* Spec §4.3 — Previous phase output upload */}
              <div data-testid="wizard-prev-phase-block"
                    style={{ marginTop: 18, padding: 16,
                              border: "1px solid var(--pf-border)",
                              borderRadius: 6, background: "var(--pf-surface-2)" }}>
                <div className="pf-overline" style={{ marginBottom: 8 }}>
                  PREVIOUS PHASE OUTPUT (OPTIONAL)
                </div>
                <p style={{ fontSize: 12, color: "var(--pf-text-muted)", margin: "0 0 10px" }}>
                  Do you have output from a completed earlier phase? Upload once and we'll pre-populate Stage A and B.1–B.2 automatically.
                </p>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[
                    { id: "none", label: "No — start fresh" },
                    { id: "rachana", label: "Yes — .rachana file" },
                    { id: "individual", label: "Yes — individual docs" },
                  ].map((m) => (
                    <button key={m.id} type="button"
                             data-testid={`wizard-prev-mode-${m.id}`}
                             onClick={() => setPrevOutputMode(m.id)}
                             style={{
                               flex: 1, padding: "6px 10px",
                               background: prevOutputMode === m.id
                                 ? "var(--pf-spark)" : "var(--pf-surface)",
                               color: prevOutputMode === m.id ? "#0E1218" : "var(--pf-fg)",
                               border: `1px solid ${prevOutputMode === m.id
                                 ? "var(--pf-spark)" : "var(--pf-border)"}`,
                               borderRadius: 3, cursor: "pointer",
                               fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                             }}>
                      {m.label}
                    </button>
                  ))}
                </div>

                {prevOutputMode === "rachana" && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8,
                                    padding: "6px 10px", background: "var(--pf-surface)",
                                    border: "1px dashed var(--pf-border)",
                                    borderRadius: 3, cursor: "pointer",
                                    fontSize: 12 }}
                          data-testid="wizard-rachana-upload-label">
                    <input type="file" accept=".zip,.json,.rachana"
                            data-testid="wizard-rachana-upload"
                            onChange={(e) => setRachanaFile(e.target.files?.[0] || null)}
                            style={{ display: "none" }} />
                    {rachanaFile
                      ? <span style={{ color: "var(--pf-ok)" }}>✓ {rachanaFile.name}</span>
                      : <span style={{ color: "var(--pf-text-muted)" }}>Click to upload .rachana file</span>}
                  </label>
                )}

                {prevOutputMode === "individual" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {[
                      { id: "dbd", label: "Design Basis Document (PDF)" },
                      { id: "pfd", label: "PFDs (PDF/DWG)" },
                      { id: "hmb", label: "H&MB / Stream Data (Excel)" },
                      { id: "equipment_list", label: "Equipment List (Excel)" },
                      { id: "plot_plan", label: "Plot Plan (PDF/DWG) — optional" },
                      { id: "preliminary_pid", label: "Preliminary P&IDs — optional" },
                      { id: "cost_estimate", label: "Cost Estimate (Excel) — optional" },
                      { id: "material_report", label: "Material Selection Report — optional" },
                    ].map((f) => (
                      <label key={f.id} style={{
                        display: "flex", alignItems: "center", gap: 6, fontSize: 11,
                        padding: "6px 10px", background: "var(--pf-surface)",
                        border: "1px dashed var(--pf-border)", borderRadius: 3,
                        cursor: "pointer",
                      }} data-testid={`wizard-prev-file-${f.id}-label`}>
                        <input type="file"
                                data-testid={`wizard-prev-file-${f.id}`}
                                onChange={(e) => setPrevFiles({ ...prevFiles,
                                                                 [f.id]: e.target.files?.[0] || null })}
                                style={{ display: "none" }} />
                        {prevFiles[f.id]
                          ? <span style={{ color: "var(--pf-ok)", fontSize: 10 }}>
                              ✓ {prevFiles[f.id].name.slice(0, 22)}
                            </span>
                          : <span style={{ color: "var(--pf-text-muted)" }}>{f.label}</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {stepDef.id === "country" && (
            <div>
              <p style={{ color: "var(--pf-text-muted)", fontSize: 13, marginTop: 0 }}>
                Sets the default code reference (Indian / US / European / Middle-East …). You can still upload custom standards next.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {meta?.regions.map((r) => {
                  const active = country === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setCountry(r.id)}
                      className="pf-card"
                      data-testid={`wizard-country-${r.id}`}
                      style={{
                        padding: 12, textAlign: "left", cursor: "pointer",
                        borderColor: active ? "var(--pf-spark)" : "var(--pf-border)",
                        background: active ? "var(--pf-surface-2)" : "var(--pf-surface)",
                      }}
                    >
                      <div className="pf-heading" style={{ fontSize: 13.5, margin: 0 }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: "var(--pf-text-muted)", marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{r.code_set}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {stepDef.id === "standards" && createdProject && (
            <div>
              <p style={{ color: "var(--pf-text-muted)", fontSize: 13, marginTop: 0 }}>
                Optionally drop your company / client specifications here. They'll take priority over the auto-loaded industry pack when the AI generates outputs. You can also add or remove these later from the project workspace.
              </p>
              <CustomStandardsPanel projectId={createdProject.id} project={createdProject} compact />
            </div>
          )}

          {stepDef.id === "review" && createdProject && (
            <div data-testid="wizard-review">
              <div className="pf-card" style={{ padding: 18 }}>
                <div className="pf-overline" style={{ marginBottom: 10 }}>SUMMARY</div>
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "10px 16px", fontSize: 13 }}>
                  <div className="pf-label" style={{ margin: 0 }}>NAME</div><div>{createdProject.name}</div>
                  <div className="pf-label" style={{ margin: 0 }}>DOMAIN</div><div>{meta?.domains.find((d) => d.id === createdProject.domain)?.name || createdProject.domain}</div>
                  <div className="pf-label" style={{ margin: 0 }}>INDUSTRY</div><div>{meta?.industries.find((i) => i.id === createdProject.industry)?.name || createdProject.industry}</div>
                  {createdProject.sub_type && (<><div className="pf-label" style={{ margin: 0 }}>SUB-TYPE</div><div data-testid="wizard-review-sub-type">{createdProject.industry_config?.sub_industry_name || createdProject.industry_config?.sub_type_name || createdProject.sub_type}</div></>)}
                  {createdProject.industry_config?.stagegate_pattern && (<><div className="pf-label" style={{ margin: 0 }}>STAGE-GATE</div><div>{createdProject.industry_config.stagegate_pattern} · {createdProject.industry_config.stagegate?.phases?.length || 0} phases</div></>)}
                  {createdProject.phase && (<><div className="pf-label" style={{ margin: 0 }}>PHASE</div><div data-testid="wizard-review-phase">{PHASE_OPTIONS.find((p) => p.id === createdProject.phase)?.name || createdProject.phase}</div></>)}
                  <div className="pf-label" style={{ margin: 0 }}>REGION</div><div>{meta?.regions.find((r) => r.id === createdProject.country)?.name || createdProject.country}</div>
                  {createdProject.description && (<><div className="pf-label" style={{ margin: 0 }}>DESCRIPTION</div><div>{createdProject.description}</div></>)}
                </div>
              </div>
              <p style={{ color: "var(--pf-text-muted)", fontSize: 13, marginTop: 14 }}>
                Project created. Stages have been scaffolded for your domain — open the project to start working.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 26px", borderTop: "1px solid var(--pf-border)", display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center", background: "var(--pf-surface-2)" }}>
          <button type="button" className="btn btn-ghost" onClick={prev} disabled={step === 0 || busy} data-testid="wizard-prev">
            <ArrowLeft size={13} /> Back
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {step < STEPS.length - 1 ? (
              <button type="button" className="btn btn-primary" onClick={next} disabled={!canNext || busy} data-testid="wizard-next">
                {busy ? <><span className="pf-spin" /> Working…</> : <>Continue <ArrowRight size={13} /></>}
              </button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={finish} data-testid="wizard-finish">
                <Check size={13} /> Open project
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
